import { baseApi } from 'shared/api/baseApi'
import { CMS_PUBLICATIONS_TAG } from 'shared/api/tags'
import type {
    CMSCreateTranslationRequest,
    CMSCreatePublicationRequest,
    CMSGenerateUploadUrlResponse,
    CMSPaginatedResponse,
    CMSPublication,
    CMSPublicationListParams,
    CMSUpdatePublicationRequest,
} from '../model/types'

export const cmsPublicationsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getCmsPublications: build.query<
            CMSPaginatedResponse<CMSPublication>,
            CMSPublicationListParams
        >({
            query: ({ organizationId, ...params }) => ({
                url: `organizations/${organizationId}/cms-publications`,
                method: 'GET',
                params,
            }),
            providesTags: [CMS_PUBLICATIONS_TAG],
        }),

        getCmsPublication: build.query<
            CMSPublication,
            { organizationId: string; publicationId: string }
        >({
            query: ({ organizationId, publicationId }) => ({
                url: `organizations/${organizationId}/cms-publications/${publicationId}`,
                method: 'GET',
            }),
            providesTags: [CMS_PUBLICATIONS_TAG],
        }),

        createCmsPublication: build.mutation<
            CMSPublication,
            CMSCreatePublicationRequest
        >({
            query: ({ organizationId, ...body }) => ({
                url: `organizations/${organizationId}/cms-publications`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [CMS_PUBLICATIONS_TAG],
        }),

        updateCmsPublication: build.mutation<
            CMSPublication,
            CMSUpdatePublicationRequest
        >({
            query: ({ organizationId, publicationId, data }) => ({
                url: `organizations/${organizationId}/cms-publications/${publicationId}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [CMS_PUBLICATIONS_TAG],
        }),

        generateCmsPublicationUploadUrl: build.mutation<
            CMSGenerateUploadUrlResponse,
            { organizationId: string; publicationId: string }
        >({
            query: ({ organizationId, publicationId }) => ({
                url: `organizations/${organizationId}/cms-publications/${publicationId}/photos/generate-upload-url`,
                method: 'POST',
            }),
        }),

        // Upload before the article is saved; the key is not tied to an id.
        generateNewCmsPublicationUploadUrl: build.mutation<
            CMSGenerateUploadUrlResponse,
            { organizationId: string }
        >({
            query: ({ organizationId }) => ({
                url: `organizations/${organizationId}/cms-publications/photos/generate-upload-url`,
                method: 'POST',
            }),
        }),

        getCmsPublicationPreviewId: build.mutation<
            { previewId: string },
            { organizationId: string; publicationId: string }
        >({
            query: ({ organizationId, publicationId }) => ({
                url: `organizations/${organizationId}/cms-publications/${publicationId}/preview`,
                method: 'POST',
            }),
        }),

        createCmsPublicationTranslation: build.mutation<
            CMSPublication,
            CMSCreateTranslationRequest
        >({
            query: ({ organizationId, id, locale }) => ({
                url: `organizations/${organizationId}/cms-publications/${id}/translations`,
                method: 'POST',
                body: { locale },
            }),
            invalidatesTags: [CMS_PUBLICATIONS_TAG],
        }),

        scheduleCmsPublication: build.mutation<
            void,
            { orgId: string; id: string; body: { releaseTime: string } }
        >({
            query: ({ orgId, id, body }) => ({
                url: `organizations/${orgId}/cms-publications/${id}/schedule`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [CMS_PUBLICATIONS_TAG],
        }),

        // Publishes the latest content right away (no scheduler round trip).
        publishCmsPublication: build.mutation<
            CMSPublication,
            { orgId: string; id: string }
        >({
            query: ({ orgId, id }) => ({
                url: `organizations/${orgId}/cms-publications/${id}/publish`,
                method: 'POST',
            }),
            invalidatesTags: [CMS_PUBLICATIONS_TAG],
        }),
    }),
})

export const {
    useGetCmsPublicationsQuery,
    useGetCmsPublicationQuery,
    useLazyGetCmsPublicationQuery,
    useCreateCmsPublicationMutation,
    useUpdateCmsPublicationMutation,
    useGenerateCmsPublicationUploadUrlMutation,
    useGenerateNewCmsPublicationUploadUrlMutation,
    useGetCmsPublicationPreviewIdMutation,
    usePublishCmsPublicationMutation,
    useScheduleCmsPublicationMutation,
    useCreateCmsPublicationTranslationMutation,
} = cmsPublicationsApi
